import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faUser } from "@fortawesome/free-regular-svg-icons";
import { Link } from "react-router-dom";
import api from "../api";
import DOMPurify from "dompurify";

function PostsTab({ loadingBarRef, hasRole }) {
    const { t } = useTranslation();
    const [posts, setPosts] = useState([]);

    const loadPendingPosts = useCallback(() => {
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }

        api.get(`${process.env.REACT_APP_API_URL}/api/admin/posts`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
        })
            .then(response => {
                setPosts(response.data);
            })
            .catch(error => console.error('Error fetching pending posts', error))
            .finally(() => {
                if (loadingBarRef.current) {
                    loadingBarRef.current.complete();
                }
            });
    }, [loadingBarRef]);

    useEffect(() => {
        loadPendingPosts();
    }, [loadPendingPosts]);

    const confirmApprovePost = (postId) => {
        Swal.fire({
            text: t('admin.confirmApprovePost'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#00ff00',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                handleApprovePost(postId);
            }
        });
    };

    const confirmDeletePost = (postId) => {
        Swal.fire({
            text: t('admin.confirmDeletePost'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ff0000',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeletePost(postId);
            }
        });
    };

    const handleApprovePost = (postId) => {
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        api.post(`${process.env.REACT_APP_API_URL}/api/admin/post/${postId}/approve`, {}, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
        })
            .then(() => {
                Swal.fire({
                    text: t('admin.postApprovedSuccessfully'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
                loadPendingPosts();
            })
            .catch(error => {
                Swal.fire({
                    text: t('admin.failedToApprovePost'),
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#ff0000',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            })
            .finally(() => {
                if (loadingBarRef.current) {
                    loadingBarRef.current.complete();
                }
            });
    };

    const handleDeletePost = (postId) => {
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        api.delete(`${process.env.REACT_APP_API_URL}/api/posts/${postId}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
        })
            .then(() => {
                Swal.fire({
                    text: t('admin.postDeletedSuccessfully'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
                loadPendingPosts();
            })
            .catch(error => {
                Swal.fire({
                    text: t('admin.failedToDeletePost'),
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#ff0000',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            })
            .finally(() => {
                if (loadingBarRef.current) {
                    loadingBarRef.current.complete();
                }
            });
    };

    const renderMedia = (media_url) => {
        if (!media_url) return null;

        // YouTube
        if (media_url.includes('youtube.com') || media_url.includes('youtu.be')) {
            let embedUrl = '';

            if (media_url.includes('/playlist?list=')) {
                const urlParams = new URLSearchParams(media_url.split('?')[1]);
                const playlistId = urlParams.get('list');
                if (playlistId) {
                    embedUrl = `https://www.youtube.com/embed/videoseries?list=${playlistId}`;
                }
            } else {
                let videoId = '';

                if (media_url.includes('/shorts/')) {
                    const path = new URL(media_url).pathname;
                    const parts = path.split('/');
                    const shortsIndex = parts.indexOf('shorts');
                    if (shortsIndex !== -1 && shortsIndex + 1 < parts.length) {
                        const videoIdWithQuery = parts[shortsIndex + 1];
                        videoId = videoIdWithQuery.split('?')[0];
                    }
                } else if (media_url.includes('youtu.be')) {
                    videoId = media_url.split('youtu.be/')[1].split('?')[0];
                } else {
                    const urlParams = new URLSearchParams(media_url.split('?')[1]);
                    videoId = urlParams.get('v');
                }

                if (videoId) {
                    embedUrl = `https://www.youtube.com/embed/${videoId}`;
                }
            }

            if (embedUrl) {
                return (
                    <iframe
                        width="560"
                        height="315"
                        src={embedUrl}
                        title={media_url.includes('/playlist?list=') ? "YouTube playlist" : "YouTube video"}
                        frameBorder="0"
                        allowFullScreen
                    ></iframe>
                );
            } else {
                return <p>Error while loading video</p>;
            }
        }

        // Twitch
        if (media_url.includes('twitch.tv') && media_url.includes('/clip/')) {
            let embedUrl = '';
            const parentDomain = 'superpeople.live';

            const clipId = media_url.split('/clip/')[1].split('?')[0];
            embedUrl = `https://clips.twitch.tv/embed?clip=${clipId}&parent=${parentDomain}&autoplay=false`;

            return (
                <iframe
                    src={embedUrl}
                    height="480"
                    width="720"
                    frameBorder="0"
                    scrolling="no"
                    allowFullScreen={true}
                    title={`Twitch clip ${clipId}`}
                ></iframe>
            );
        } else if (media_url.includes('twitch.tv/videos/')) {
            const parentDomain = 'superpeople.live';
            const videoId = media_url.split('videos/')[1].split('?')[0];

            const embedUrl = `https://player.twitch.tv/?video=${videoId}&parent=${parentDomain}&autoplay=false`;

            return (
                <iframe
                    src={embedUrl}
                    height="480"
                    width="720"
                    frameBorder="0"
                    scrolling="no"
                    allowFullScreen={true}
                    title={`Twitch video ${videoId}`}
                ></iframe>
            );
        }

        // Image
        if (media_url.match(/\.(jpeg|jpg|gif|png)$/i)) {
            return (
                <Zoom>
                    <img
                        src={media_url}
                        alt={t('userProfile.userMedia')}
                        style={{ cursor: 'zoom-in', maxWidth: '600px', maxHeight: '400px' }}
                    />
                </Zoom>
            );
        }

        // Video
        if (media_url.match(/\.(mp4|webm|ogg)$/i)) {
            return (
                <video controls width="600">
                    <source src={media_url} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
            );
        }

        // Default
        return (
            <a href={media_url} target="_blank" rel="noopener noreferrer">
                {media_url}
            </a>
        );
    };

    const formatDate = (isoDate) => {
        if (!isoDate) return '';
        const date = new Date(isoDate);
        return date.toLocaleDateString('en', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            minute: 'numeric',
            hour: 'numeric',
            second: 'numeric',
        });
    };

    return (
        <div className="admin-posts-wrapper">
            <div className="admin-section-title">
                <span className="title-text">{t('admin.postsAwaitingApproval')}</span>
                <span className="arrow-icon">›</span>
            </div>
            <div className="admin-posts-list">
                {posts.length === 0 ? (
                    <div className="admin-no-posts-message">
                        <p>{t('admin.noPendingPosts')}</p>
                    </div>
                ) : (
                    posts.map(post => (
                        <div key={post.id} className="admin-post-card">
                            <div className="admin-post-content">
                                <div className="admin-post-creator">
                                    <FontAwesomeIcon icon={faUser}/>
                                    <Link to={`/user/${post.user_id}`} className="admin-post-username">
                                        {post.username}
                                    </Link>
                                </div>
                                <div className="admin-post-info"
                                     dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(post.content)}}
                                ></div>
                                <div className="admin-post-media">
                                    {renderMedia(post.media_url)}
                                </div>
                                <div className="admin-post-date">
                                    <FontAwesomeIcon icon={faCalendarAlt}/> {formatDate(post.created_at)}
                                </div>
                            </div>
                            <div className="admin-post-actions">
                                <button className="admin-approve-button"
                                        onClick={() => confirmApprovePost(post.id)}>✔️ {t('admin.approvePost')}</button>
                                {hasRole('ROLE_ADMIN') && (
                                    <button className="admin-post-delete-button"
                                            onClick={() => confirmDeletePost(post.id)}>🧹 {t('admin.deletePost')}</button>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default PostsTab;
